/**
 * CampaignFormContainer
 *
 * Orchestrates the multi-step create-campaign flow, rendering step pages and
 * wiring navigation, validation, media handling, and survey questions.
 */
import React from 'react';
import { Card, CardContent } from '../../../components/ui/layout/card';
import { IntroductionPage } from '../steps/IntroductionPage';
import { InternalName } from '../steps/InternalName';
import { DesignPage } from '../steps/DesignPage';
import { BasicInfo } from '../steps/BasicInfo';
import { CampaignDetails } from '../steps/CampaignDetails';
import { ReviewCampaign } from '../steps/ReviewCampaign';
import { StepNavigation } from '../navigation/StepNavigation';
import { ErrorMessage } from './ErrorMessage';

export const CampaignFormContainer = ({
  currentStep,
  formData,
  handleInputChange,
  updateFormData,
  aiGeneratedFields,
  setAiGeneratedFields,
  surveyQuestions,
  setSurveyQuestions,
  handleNext,
  handlePrevious,
  handleSubmit,
  isSubmitting,
  isSavingDraft,
  handleSaveDraft,
  isEditingDraft,
  setIsAIModalOpen,
  handleUseTemplate,
  clearSelectedTemplate,
  selectedTemplate,
  selectedTheme,
  setSelectedTheme,
  gradientColors,
  setGradientColors,
  gradientDirection,
  setGradientDirection,
  hexText,
  setHexText,
  themes,
  previewImage,
  handleImageChange,
  handleRemoveImage,
  setIsHelpOpen,
  hasExplainerVideo,
  setHasExplainerVideo,
  explainerVideo,
  setExplainerVideo,
  error,
  currentNamespace,
  userPermission,
  renderCustomButtons = false,
  renderCustomButtonsContent = null
}) => {
  // Log important props on mount
  React.useEffect(() => {
    console.log('CampaignFormContainer mounted with props:', {
      handleImageChange: typeof handleImageChange === 'function' ? 'function' : typeof handleImageChange,
      handleRemoveImage: typeof handleRemoveImage === 'function' ? 'function' : typeof handleRemoveImage,
      previewImage: previewImage ? 'exists' : 'null'
    });
  }, []);

  // Helper function to display permission in a user-friendly way
  const formatPermission = (permission) => {
    if (!permission) return 'No Access';
    
    switch(permission) {
      case 'admin':
        return 'Admin (Full Access)';
      case 'read/write':
        return 'Editor (Read/Write)';
      case 'readonly':
        return 'Viewer (Read Only)';
      default:
        return permission;
    }
  };

  return (
    <Card className="w-full">
      <CardContent className={currentStep === 0 ? "p-6 pb-2" : ""}>
        <form onSubmit={(e) => {
          // Always prevent default form submission
          e.preventDefault();
          console.log("Form submission handled by onSubmit prop in StepNavigation");
          // Form submission is now handled by the onSubmit prop in StepNavigation
        }} className="space-y-8">
          {currentStep === 0 && (
            <div className="space-y-6">
              <IntroductionPage />
              {renderCustomButtonsContent}
            </div>
          )}

          {currentStep === 1 && (
            <InternalName
              formData={formData}
              handleInputChange={handleInputChange}
              aiGeneratedFields={aiGeneratedFields}
            />
          )}

          {currentStep === 2 && (
            <DesignPage
              selectedTheme={selectedTheme}
              setSelectedTheme={setSelectedTheme}
              gradientColors={gradientColors}
              setGradientColors={setGradientColors}
              gradientDirection={gradientDirection}
              setGradientDirection={setGradientDirection}
              hexText={hexText}
              setHexText={setHexText}
              themes={themes}
            />
          )}

          {currentStep === 3 && (
            <BasicInfo
              formData={formData}
              handleInputChange={handleInputChange}
              previewImage={previewImage}
              handleImageChange={typeof handleImageChange === 'function' ? handleImageChange : (e, setErr) => {
                console.error('handleImageChange is not a function in CampaignFormContainer');
                if (typeof setErr === 'function') {
                  setErr({ type: 'error', message: 'Image upload functionality is not available. Please try again later.' });
                }
              }}
              handleRemoveImage={handleRemoveImage}
              aiGeneratedFields={aiGeneratedFields}
            />
          )}

          {currentStep === 4 && (
            <CampaignDetails
              formData={formData}
              handleInputChange={handleInputChange}
              surveyQuestions={surveyQuestions}
              handleAddQuestion={() => {
                // Limit to a maximum of 3 questions
                if (surveyQuestions.length >= 3) {
                  return;
                }
                
                // Add new question with a unique ID
                const newQuestion = { 
                  id: surveyQuestions.length + 1, 
                  question: '' // Explicitly set to empty string, not null or undefined
                };
                
                const updatedQuestions = [...surveyQuestions, newQuestion];
                
                console.log('Adding new question:', {
                  newQuestion,
                  totalQuestions: updatedQuestions.length,
                  questionsDetails: updatedQuestions.map(q => ({
                    id: q.id,
                    hasContent: Boolean(q.question && q.question.trim()),
                    contentPreview: q.question !== null && q.question !== undefined ? 
                      (q.question.length > 20 ? q.question.substring(0, 20) + '...' : q.question) : 'null/undefined'
                  }))
                });
                
                setSurveyQuestions(updatedQuestions);
                
                // Ensure this new question doesn't have the AI generated indicator
                const newQuestionIndex = surveyQuestions.length;
                const fieldName = `question_${newQuestionIndex}`;
                
                // If aiGeneratedFields has this field, explicitly set it to false
                if (aiGeneratedFields && Object.keys(aiGeneratedFields).includes(fieldName)) {
                  const updatedAiGeneratedFields = {
                    ...aiGeneratedFields,
                    [fieldName]: false
                  };
                  setAiGeneratedFields(updatedAiGeneratedFields);
                }
              }}
              handleRemoveQuestion={(id) => {
                setSurveyQuestions(surveyQuestions.filter(q => q.id !== id));
              }}
              handleQuestionChange={(id, value) => {
                // Check if this question was AI generated
                const questionIndex = surveyQuestions.findIndex(q => q.id === id);
                const fieldName = `question_${questionIndex}`;
                
                // When user edits a question, remove it from AI generated fields
                if (aiGeneratedFields && aiGeneratedFields[fieldName]) {
                  // Create a new aiGeneratedFields object with the updated field
                  const updatedAiGeneratedFields = {
                    ...aiGeneratedFields,
                    [fieldName]: false
                  };
                  // Call the setter function directly
                  setAiGeneratedFields(updatedAiGeneratedFields);
                }
                
                setSurveyQuestions(
                  surveyQuestions.map(q => q.id === id ? { ...q, question: value } : q)
                );
              }}
              setIsHelpOpen={setIsHelpOpen}
              aiGeneratedFields={aiGeneratedFields}
              hasExplainerVideo={hasExplainerVideo}
              setHasExplainerVideo={setHasExplainerVideo}
              explainerVideo={explainerVideo}
              setExplainerVideo={setExplainerVideo}
            />
          )}

          {currentStep === 5 && (
            <ReviewCampaign
              formData={formData}
              handleInputChange={handleInputChange}
              aiGeneratedFields={aiGeneratedFields}
              surveyQuestions={surveyQuestions}
              previewImage={previewImage}
              selectedTheme={selectedTheme}
              themes={themes}
              hasExplainerVideo={hasExplainerVideo}
              explainerVideo={explainerVideo}
              currentNamespace={currentNamespace}
              userPermission={userPermission}
            />
          )}

          <div className="mt-8">
            <ErrorMessage error={error} />

            <StepNavigation
              currentStep={currentStep}
              totalSteps={6}
              onPrevious={handlePrevious}
              onNext={handleNext}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
              isSavingDraft={isSavingDraft}
              handleSaveDraft={handleSaveDraft}
              isEditingDraft={isEditingDraft}
              formData={{
                ...formData,
                hasExplainerVideo,
                explainerVideo
              }}
              surveyQuestions={surveyQuestions}
              setIsAIModalOpen={setIsAIModalOpen}
              onUseTemplate={handleUseTemplate}
              clearSelectedTemplate={clearSelectedTemplate}
              selectedTemplate={selectedTemplate}
              renderCustomButtons={renderCustomButtons}
            />
          </div>
        </form>
      </CardContent>
    </Card>
  );
}; 